/**
 * @fileoverview Slide-out AI assistant panel for FireSquirrel.
 * @module @firesquirrel/ai-chatbot
 *
 * Responsibilities:
 * - Render the slide-out chat panel (header, message list, quick actions,
 *   input area) when the chat is open.
 * - Wire message send, PDF upload, session clear, and quick-action
 *   selection through `useAIChat()`.
 * - Provide drag-to-resize on the panel's left edge (320–600px).
 *
 * Dependencies:
 * - React (peer), lucide-react icons.
 * - `useAIChat()` from `../contexts/AIChatContext` — sole data source.
 * - `AIChatMessage`, `AIQuickActions` child components.
 *
 * Architecture:
 * - Local component state covers UI-only concerns (input draft, selected
 *   file, panel width, quick-action collapse). Session state (messages,
 *   isProcessing, error) is owned by `AIChatContext`; this component never
 *   calls fetch/apiClient directly.
 *
 * Notes:
 * - Per `.devin/rules/ai-implementation-standard.md`, chat history is
 *   in-memory only (legacy exception — it should be persisted to Dexie);
 *   this is a documentation note only, no behavior change.
 * - Returns null when `isOpen` is false.
 */

import { useEffect, useRef, useState } from 'react';
import { Minus, Send, RotateCcw, ChevronDown, ChevronUp, Paperclip, FileText, XCircle } from 'lucide-react';
import { useAIChat } from '../contexts/AIChatContext';
import AIChatMessage from './AIChatMessage';
import AIQuickActions from './AIQuickActions';
import squirrelIconLarge from '../assets/squirrel_icon_large.png';

/**
 * @component AIChatbot
 * Purpose:     Slide-out panel hosting the FireSquirrel AI assistant
 *              conversation, quick actions, PDF upload, and message input.
 * Props:       (none — reads everything from `useAIChat()`).
 * Behavior:    Renders null while closed; auto-scrolls to the latest
 *              message; focuses the input on open; Enter sends,
 *              Shift+Enter newline; PDF selection validated to
 *              application/pdf and ≤50MB.
 * Data Source: in-memory session state from `AIChatContext` (hybrid — UI
 *              state local, session state via `useAIChat()`).
 * Constraints: no direct API calls; all server interaction goes through
 *              the context's sendMessage/uploadPDF actions.
 * Performance: cheap — a few `useEffect`s for focus/scroll and a
 *              document-level mousemove listener only while resizing.
 */
export default function AIChatbot() {
  const {
    isOpen,
    session,
    closeChat,
    sendMessage,
    clearSession,
    startReportCreation,
    uploadPDF,
    appContext,
  } = useAIChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [selectedQuickAction, setSelectedQuickAction] = useState<string | undefined>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [width, setWidth] = useState(384);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.messages]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !resizeRef.current) return;
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= 320 && newWidth <= 600) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = inputRef.current;
    
    // Allow submit if either message or file is present
    if (!input?.value.trim() && !selectedFile) return;

    const message = input?.value.trim() || '';
    
    if (selectedFile) {
      // Handle file upload
      setUploadingFile(true);
      try {
        await uploadPDF(selectedFile);
        handleRemoveFile();
      } catch (error) {
        console.error('File upload failed:', error);
      } finally {
        setUploadingFile(false);
      }
    } else {
      // Regular text message
      await sendMessage(message);
    }

    if (input) {
      input.value = '';
      input.style.height = 'auto';
    }
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
    setInputValue(target.value);
  };

  const handleQuickActionSelect = (action: { id: string; reportType: 'report' | 'dashboard' | 'document'; promptMessage: string }) => {
    setSelectedQuickAction(action.id);
    startReportCreation(action.reportType, action.promptMessage);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('File size must be less than 50MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <div ref={resizeRef} className="fixed top-0 right-0 bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col overflow-hidden animate-slide-in" style={{ width: `${width}px`, bottom: '4px' }}>
      {/* Resize Handle */}
      <div
        onMouseDown={() => setIsResizing(true)}
        className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-orange-500 transition-colors z-10"
        style={{ marginLeft: '-1px' }}
        title="Drag to resize"
      />
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200" style={{ background: 'linear-gradient(90deg, #f97316 0%, #ea580c 50%, #dc2626 100%)' }}>
        <div className="flex items-center gap-3 text-white">
          <img src={squirrelIconLarge} alt="AI Squirrel" className="w-8 h-8 drop-shadow-lg animate-bounce-gentle" />
          <div>
            <h2 className="font-semibold text-lg">FireSquirrel AI</h2>
            <p className="text-xs text-white/80">Your Smart Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {session && session.messages.length > 0 && (
            <button
              onClick={clearSession}
              className="text-white hover:bg-white/20 rounded-sm p-1 transition-colors"
              aria-label="New Chat"
              title="Start New Chat"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={closeChat}
            className="text-white hover:bg-white/20 rounded-sm p-1 transition-colors"
            aria-label="Minimize AI Assistant"
            title="Minimize"
          >
            <Minus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Quick Actions — creation surfaces only; the customer portal gets
          suggested questions instead (D-0.13). */}
      {appContext === 'customer-portal' ? (
        (!session || !session.messages.some((m) => m.role === 'user')) && (
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600 mb-3">What can I help you with?</p>
            <div className="flex flex-col gap-2">
              {(session?.conversationContext?.customContext?.violation
                ? [
                    'What does this violation code mean?',
                    'How do I fix this violation?',
                    'How long do I have to correct it?',
                  ]
                : [
                    'What violations were found on my property?',
                    'How do I renew a permit?',
                    'How do I submit a self-certification?',
                  ]
              ).map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => { setInputValue(q); void sendMessage(q); }}
                  className="text-left text-sm text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg px-3 py-2 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )
      ) : (!session || session.messages.length === 0) ? (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600 mb-3">What would you like to create?</p>
          <AIQuickActions
            selectedAction={selectedQuickAction}
            onSelectAction={handleQuickActionSelect}
          />
        </div>
      ) : (
        <div className="border-b border-gray-200">
          <button
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-sm text-gray-700"
          >
            <span className="font-medium">Quick Actions</span>
            {showQuickActions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showQuickActions && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
              <AIQuickActions
                selectedAction={selectedQuickAction}
                onSelectAction={handleQuickActionSelect}
              />
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-w-0">
        {session?.messages.length === 0 && (
          <div className="text-center py-8 animate-fade-in">
            <div className="relative inline-block mb-4">
              <img 
                src={squirrelIconLarge} 
                alt="AI Squirrel" 
                className="w-24 h-24 mx-auto drop-shadow-2xl animate-float" 
              />
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-orange-500 text-white text-xs font-medium rounded-full shadow-lg">
                Ready to help!
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 mt-6">
              Welcome to FireSquirrel AI
            </h3>
            <p className="text-sm text-gray-600 max-w-xs mx-auto leading-relaxed">
              {appContext === 'customer-portal'
                ? 'I can answer questions about your inspections, violations, permits, and how to use this portal.'
                : 'I can help you create data reports, dashboards, and document templates using natural language.'}
            </p>
            {appContext === 'system' && (
              <div className="mt-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg max-w-sm mx-auto">
                <p className="text-xs text-blue-900 font-medium mb-1">💡 Department-Specific Questions</p>
                <p className="text-xs text-blue-700 leading-relaxed">
                  To ask about a specific department, mention the department name in <code className="px-1 py-0.5 bg-blue-100 rounded-sm text-blue-900">[brackets]</code> or <code className="px-1 py-0.5 bg-blue-100 rounded-sm text-blue-900">'quotes'</code>
                </p>
                <p className="text-xs text-blue-600 mt-2 italic">
                  Example: "How many inspections did [Riverside Fire] complete today?"
                </p>
              </div>
            )}
          </div>
        )}

        {session?.messages.map((message) => (
          <AIChatMessage key={message.id} message={message} />
        ))}

        {session?.isProcessing && (
          <div className="flex items-center gap-3 text-gray-500 bg-gray-50 rounded-lg p-3">
            <img src={squirrelIconLarge} alt="Thinking" className="w-8 h-8 animate-spin-gentle" />
            <div className="flex-1">
              <div className="flex gap-1 mb-1">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm font-medium">Fire Squirrel is analyzing your request...</span>
            </div>
          </div>
        )}

        {session?.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{session.error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-gray-200 p-4">
        {/* Selected File Display */}
        {selectedFile && (
          <div className="mb-2 flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
            <FileText className="w-4 h-4 text-orange-600 shrink-0" />
            <span className="text-sm text-gray-700 truncate flex-1">{selectedFile.name}</span>
            <span className="text-xs text-gray-500 shrink-0">
              {(selectedFile.size / 1024).toFixed(0)} KB
            </span>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
              aria-label="Remove file"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="flex items-end gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {/* Paperclip button - only show for Document Template */}
          {(selectedQuickAction === 'document-template' || session?.conversationContext?.reportType === 'document') && (
            <button
              type="button"
              onClick={handleFileUploadClick}
              disabled={session?.isProcessing || uploadingFile}
              className="text-gray-500 hover:text-orange-600 rounded-lg p-2 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              aria-label="Upload PDF"
              title="Upload PDF for document template analysis"
            >
              <Paperclip className="w-5 h-5" />
            </button>
          )}

          <textarea
            ref={inputRef}
            placeholder={selectedFile ? "Add a message (optional)..." : "Ask me anything or describe what you want to create..."}
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            rows={1}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            disabled={session?.isProcessing || uploadingFile}
          />
          <button
            type="submit"
            disabled={session?.isProcessing || uploadingFile || (!inputValue.trim() && !selectedFile)}
            className="text-white rounded-lg px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105 shrink-0"
            style={{ background: 'linear-gradient(90deg, #f97316 0%, #dc2626 100%)' }}
            onMouseEnter={(e) => !session?.isProcessing && !uploadingFile && (e.currentTarget.style.background = 'linear-gradient(90deg, #ea580c 0%, #b91c1c 100%)')}
            onMouseLeave={(e) => !session?.isProcessing && !uploadingFile && (e.currentTarget.style.background = 'linear-gradient(90deg, #f97316 0%, #dc2626 100%)')}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {selectedFile ? (
            <>📎 PDF selected. Click send to analyze and generate template.</>
          ) : (selectedQuickAction === 'document-template' || session?.conversationContext?.reportType === 'document') ? (
            <>Press Enter to send, Shift+Enter for new line. Click 📎 to upload PDF.</>
          ) : (
            <>Press Enter to send, Shift+Enter for new line.</>
          )}
        </p>
        {appContext === 'customer-portal' && (
          <p className="text-[11px] text-gray-400 mt-1 leading-snug">
            FireSquirrel's answers are informational, not legal advice. For legal
            questions, ask an inspector or contact the department directly.
          </p>
        )}
      </form>
    </div>
  );
}
